/**
 * ground.ts
 *
 * Normalises four pitch vertices (any winding order, any rotation, local or GPS)
 * into canonical corners A B C D:
 *
 *   B ──────── C
 *   |  (long)  |
 *   A ──────── D
 *
 *   AB = CD  short sides   BC = DA  long sides
 */

import type {
  Point2D,
  GpsPoint,
  VertexInput,
  NormalizedGround,
  CoordType,
  GroundContext,
  BuildGroundContextOptions,
  CanonicalPoint,
  CanvasPoint,
  Viewport,
  GpsProjector,
  HomographyMatrix, GroundVertices,
} from './types';
import { GroundType } from '../../../models/ground';

// ─── INTERNAL HELPERS ────────────────────────────────────────────────────────

function dist(p1: Point2D, p2: Point2D): number {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}

function signedArea(pts: Point2D[]): number {
  let area = 0;
  const n = pts.length;
  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    area += pts[i].x * pts[j].y;
    area -= pts[j].x * pts[i].y;
  }
  return area / 2;
}

function centroid(pts: Point2D[]): Point2D {
  return {
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
  };
}

function isGpsPoint(v: VertexInput): v is GpsPoint {
  return 'lat' in v;
}

// ─── GPS PROJECTION ──────────────────────────────────────────────────────────

/**
 * Convert an array of GPS points to local metric coordinates (metres) using an
 * equirectangular projection centred on the array's mean position.
 * Accurate to < 1 m over football-pitch distances.
 */
export function gpsToLocal(gpsPoints: GpsPoint[]): Point2D[] {
  const R = 6_371_000;
  const latC = gpsPoints.reduce((s, p) => s + p.x, 0) / gpsPoints.length;
  const lonC = gpsPoints.reduce((s, p) => s + p.y, 0) / gpsPoints.length;
  const latRad = (latC * Math.PI) / 180;

  return gpsPoints.map((p) => ({
    x: ((p.y - lonC) * Math.PI * R * Math.cos(latRad)) / 180,
    y: ((p.x - latC) * Math.PI * R) / 180,
  }));
}

/**
 * Like gpsToLocal but also returns the centroid anchor used as projection
 * origin, so callers can reuse it for player data.
 */
export function gpsToLocalWithAnchor(
  gpsPoints: GpsPoint[],
): { local: Point2D[]; anchor: GpsPoint } {
  const R = 6_371_000;
  const lat = gpsPoints.reduce((s, p) => s + p.x, 0) / gpsPoints.length;
  const lon = gpsPoints.reduce((s, p) => s + p.y, 0) / gpsPoints.length;
  const latRad = (lat * Math.PI) / 180;
  const anchor: GpsPoint = { x: lat, y: lon };

  const local = gpsPoints.map((p) => ({
    x: ((p.y - lon) * Math.PI * R * Math.cos(latRad)) / 180,
    y: ((p.x - lat) * Math.PI * R) / 180,
  }));

  return { local, anchor };
}

/**
 * Build a reusable GPS→local projector anchored on a fixed origin.
 * Use this when player data and ground vertices share the same GPS frame.
 */
export function makeGpsProjector(anchor: GpsPoint): GpsProjector {
  const R = 6_371_000;
  const latRad = (anchor.x * Math.PI) / 180;
  return (lat: number, lon: number): Point2D => ({
    x: ((lon - anchor.y) * Math.PI * R * Math.cos(latRad)) / 180,
    y: ((lat - anchor.x) * Math.PI * R) / 180,
  });
}

// ─── LABEL ASSIGNMENT ────────────────────────────────────────────────────────

function assignLabels(localPts: Point2D[]): Pick<NormalizedGround, 'A' | 'B' | 'C' | 'D'> {
  if (localPts.length !== 4) throw new Error('Exactly 4 vertices required.');

  // Sort CCW around centroid
  const c = centroid(localPts);
  const sorted = [...localPts].sort(
    (a, b) => Math.atan2(a.y - c.y, a.x - c.x) - Math.atan2(b.y - c.y, b.x - c.x),
  );
  if (signedArea(sorted) < 0) sorted.reverse();

  // Find a short side followed by a long side → starting vertex
  const sideLengths = sorted.map((p, i) => dist(p, sorted[(i + 1) % 4]));
  const avgLen = sideLengths.reduce((a, b) => a + b, 0) / 4;

  let startIdx = 0;
  for (let i = 0; i < 4; i += 1) {
    if (sideLengths[i] < avgLen && sideLengths[(i + 1) % 4] >= avgLen) {
      startIdx = i;
      break;
    }
  }

  const ring = Array.from({ length: 4 }, (_, i) => sorted[(startIdx + i) % 4]);
  const [P0, P1, P2, P3] = ring;

  // ring[0] → A (bottom-left): must have lower y than ring[1] (B, top-left)
  if (P0.y > P1.y) {
    return {
      A: P2, B: P3, C: P0, D: P1,
    };
  }
  return {
    A: P0, B: P1, C: P2, D: P3,
  };
}

// ─── HOMOGRAPHY (PERSPECTIVE CORRECTION) ────────────────────────────────────
//
// A projective homography H is a 3×3 matrix that maps homogeneous 2D coords:
//
//   [u']   [h0 h1 h2] [x]
//   [v'] = [h3 h4 h5] [y]
//   [w']   [h6 h7 h8] [1]
//
//   u_canonical = u'/w',  v_canonical = v'/w'
//
// We solve for H given the four correspondences:
//   A → (0,0),  D → (1,0),  C → (1,1),  B → (0,1)
// using the standard DLT (Direct Linear Transform) algorithm (8 equations, 8
// unknowns with h8 = 1 fixed).

/** Solve Ax = b via Gaussian elimination (A is n×n, b is n×1). Returns x. */
function gaussianElimination(A: number[][], b: number[]): number[] {
  const n = b.length;
  // Augmented matrix [A | b]
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col += 1) {
    // Partial pivot
    let maxRow = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row;
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];

    if (Math.abs(M[col][col]) < 1e-12) throw new Error('Singular matrix — cannot compute homography.');

    for (let row = col + 1; row < n; row += 1) {
      const factor = M[row][col] / M[col][col];
      for (let k = col; k <= n; k += 1) M[row][k] -= factor * M[col][k];
    }
  }

  // Back-substitution
  const x = new Array<number>(n).fill(0);
  for (let i = n - 1; i >= 0; i -= 1) {
    x[i] = M[i][n];
    for (let j = i + 1; j < n; j += 1) x[i] -= M[i][j] * x[j];
    x[i] /= M[i][i];
  }
  return x;
}

/**
 * Compute the projective homography that maps the four ground corners to the
 * unit square:
 *   A → (0,0)   B → (0,1)   C → (1,1)   D → (1,0)
 *
 * Uses the DLT algorithm: builds an 8×8 linear system from the four point
 * correspondences and solves for the 8 free parameters of H (h8 = 1).
 */
export function computeHomography(
  A: Point2D,
  B: Point2D,
  C: Point2D,
  D: Point2D,
): HomographyMatrix {
  // Four correspondences: src point (x,y) → dst point (u,v)
  const pairs: Array<[Point2D, CanonicalPoint]> = [
    [A, { u: 0, v: 0 }],
    [B, { u: 0, v: 1 }],
    [C, { u: 1, v: 1 }],
    [D, { u: 1, v: 0 }],
  ];

  // Build 8×8 system from DLT (2 equations per correspondence)
  const Amat: number[][] = [];
  const bvec: number[] = [];

  for (const [src, dst] of pairs) {
    const { x, y } = src;
    const { u, v } = dst;
    // Row for u equation:  -x*h0 - y*h1 - h2 + u*x*h6 + u*y*h7 = -u  (h8=1)
    Amat.push([-x, -y, -1, 0, 0, 0, u * x, u * y]);
    bvec.push(-u);
    // Row for v equation:   x*h3 + y*h4 + h5 - v*x*h6 - v*y*h7 = v   (h8=1)
    Amat.push([0, 0, 0, -x, -y, -1, v * x, v * y]);
    bvec.push(-v);
  }

  const h = gaussianElimination(Amat, bvec);

  return [
    h[0], h[1], h[2],
    h[3], h[4], h[5],
    h[6], h[7], 1,
  ];
}

/**
 * Apply a homography matrix to a point.
 * Returns canonical {u, v} ∈ (approximately) [0,1]².
 */
export function applyHomography(H: HomographyMatrix, pt: Point2D): CanonicalPoint {
  const [h0, h1, h2, h3, h4, h5, h6, h7, h8] = H;
  const w = h6 * pt.x + h7 * pt.y + h8;
  return {
    u: (h0 * pt.x + h1 * pt.y + h2) / w,
    v: (h3 * pt.x + h4 * pt.y + h5) / w,
  };
}

/**
 * Measure the maximum deviation from 90° of the four inner angles of a
 * quadrilateral defined by corners A B C D (in canonical order).
 * Returns degrees.
 */
export function maxAngleError(A: Point2D, B: Point2D, C: Point2D, D: Point2D): number {
  const corners: Array<[Point2D, Point2D, Point2D]> = [
    [D, A, B], // angle at A
    [A, B, C], // angle at B
    [B, C, D], // angle at C
    [C, D, A], // angle at D
  ];
  let max = 0;
  for (const [prev, cur, next] of corners) {
    const v1 = { x: prev.x - cur.x, y: prev.y - cur.y };
    const v2 = { x: next.x - cur.x, y: next.y - cur.y };
    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag = Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y);
    const angleDeg = (Math.acos(Math.max(-1, Math.min(1, dot / mag))) * 180) / Math.PI;
    const err = Math.abs(angleDeg - 90);
    if (err > max) max = err;
  }
  return +max.toFixed(4);
}

// ─── PUBLIC API ──────────────────────────────────────────────────────────────

/**
 * Normalise four pitch vertices into canonical corners A B C D.
 *
 * @param points    Four vertices — either all `{x, y}` (local) or all `{lat, lon}` (GPS).
 * @param coordType `"local"` | `"gps"` | `"auto"` (default: auto-detected from shape).
 */

/**
 * Normalise four pitch vertices into canonical corners A B C D.
 *
 * @param points    Four vertices — either all `{x, y}` (local) or all `{lat, lon}` (GPS).
 * @param coordType `"local"` | `"gps"` | `"auto"` (default: auto-detected from shape).
 */
export function normalizeGroundVertices(
  points: VertexInput[],
  coordType: CoordType | 'auto' = 'auto',
): NormalizedGround {
  if (!Array.isArray(points) || points.length !== 4) {
    throw new Error('Input must be an array of exactly 4 points.');
  }

  const type: CoordType = coordType === 'auto'
    ? isGpsPoint(points[0])
      ? 'gps'
      : 'local'
    : coordType;

  let local: Point2D[];
  let gpsAnchor: GpsPoint | null = null;

  if (type === 'gps') {
    const result = gpsToLocalWithAnchor(points as GpsPoint[]);
    local = result.local;
    gpsAnchor = result.anchor;
  } else {
    local = (points as Point2D[]).map((p) => ({ x: p.x, y: p.y }));
  }

  const {
    A, B, C, D,
  } = assignLabels(local);

  const shortSide = (dist(A, B) + dist(C, D)) / 2;
  const longSide = (dist(B, C) + dist(D, A)) / 2;
  const rotationRad = Math.atan2(C.y - B.y, C.x - B.x);

  const angleErr = maxAngleError(A, B, C, D);

  return {
    A,
    B,
    C,
    D,
    meta: {
      inputType: type,
      shortSide: +shortSide.toFixed(4),
      longSide: +longSide.toFixed(4),
      aspectRatio: +(longSide / shortSide).toFixed(4),
      rotationDeg: +((rotationRad * 180) / Math.PI).toFixed(4),
      maxAngleErrorDeg: angleErr,
      correctPerspective: false, // set by buildGroundContext
      gpsAnchor, // null when local input, centroid when GPS input
    },
  };
}

/** Shorthand: local `{x, y}` vertices */
export function normalizeLocalGround(points: Point2D[]): NormalizedGround {
  return normalizeGroundVertices(points, 'local');
}

/** Shorthand: GPS `{lat, lon}` vertices */
export function normalizeGPSGround(points: GpsPoint[]): NormalizedGround {
  return normalizeGroundVertices(points, 'gps');
}

// ─── TRANSFORM CONTEXT ───────────────────────────────────────────────────────

/**
 * Build the transform context from a normalised ground.
 *
 * The context encodes the pitch's local orthonormal frame so that any point can
 * be projected to canonical [0,1]² — and then to canvas pixels — without
 * knowing the rotation angle explicitly.
 */
export function buildGroundContext(
  ground: NormalizedGround,
  opts: BuildGroundContextOptions = {},
): GroundContext {
  const {
    A, B, C, D,
  } = ground;
  const correct = opts.correctPerspective ?? false;

  const longVec = { x: D.x - A.x, y: D.y - A.y };
  const longLen = Math.hypot(longVec.x, longVec.y);
  const longUnit = { x: longVec.x / longLen, y: longVec.y / longLen };

  const shortVec = { x: B.x - A.x, y: B.y - A.y };
  const shortLen = Math.hypot(shortVec.x, shortVec.y);
  const shortUnit = { x: shortVec.x / shortLen, y: shortVec.y / shortLen };

  let homography;
  try {
    homography = correct ? computeHomography(A, B, C, D) : null;
  } catch (error) {
    console.error('Failed to compute homography for ground', ground, error);
    homography = null;
  }

  // Stamp the flag back onto meta so callers can inspect it
  // eslint-disable-next-line no-param-reassign
  ground.meta.correctPerspective = correct;

  return {
    ground,
    A,
    B,
    C,
    D,
    longLen,
    shortLen,
    longUnit,
    shortUnit,
    // Use the caller-supplied anchor if provided, otherwise fall back to the
    // one recorded in meta when the ground was built from GPS vertices.
    // This guarantees ground and players are always projected from the same origin.
    gpsProjector: (() => {
      const anchor = opts.gpsAnchor ?? ground.meta.gpsAnchor ?? null;
      return anchor ? makeGpsProjector(anchor) : null;
    })(),
    homography,
    correctPerspective: correct,
  };
}

// ─── COORDINATE TRANSFORMS ───────────────────────────────────────────────────

/**
 * Local `{x, y}` → canonical `{u, v}` ∈ [0,1]²
 *
 * u = 0 → left side (AB)    u = 1 → right side (DC)
 * v = 0 → bottom (AD)       v = 1 → top (BC)
 */
/**
 * Local {x,y} → canonical {u,v} ∈ [0,1]²
 *
 * When ctx.correctPerspective is true, applies the projective homography so
 * that non-rectangular ground shapes are corrected to a perfect rectangle.
 * Otherwise uses the fast orthogonal dot-product projection.
 */
export function localToCanonical(pt: Point2D, ctx: GroundContext): CanonicalPoint {
  if (ctx.homography) {
    return applyHomography(ctx.homography, pt);
  }
  const dx = pt.x - ctx.A.x;
  const dy = pt.y - ctx.A.y;

  return {
    u: (dx * ctx.longUnit.x + dy * ctx.longUnit.y) / ctx.longLen,
    v: (dx * ctx.shortUnit.x + dy * ctx.shortUnit.y) / ctx.shortLen,
  };
}

/**
 * Canonical `{u, v}` → canvas pixel `{cx, cy}`.
 * v is flipped because canvas y grows downward.
 */
export function canonicalToCanvas(uv: CanonicalPoint, vp: Viewport): CanvasPoint {
  return {
    cx: vp.x + uv.u * vp.w,
    cy: vp.y + (1 - uv.v) * vp.h,
  };
}

/**
 * Compute the pitch viewport inside a canvas, letterboxed to preserve aspect ratio.
 */
export function computeViewport(
  canvasW: number,
  canvasH: number,
  ctx: GroundContext,
  padding: number,
): Viewport {
  const availW = canvasW - 2 * padding;
  const availH = canvasH - 2 * padding;
  const aspect = ctx.longLen / ctx.shortLen;

  let w: number; let
    h: number;
  if (availW / availH > aspect) {
    h = availH;
    w = h * aspect;
  } else {
    w = availW;
    h = w / aspect;
  }

  return {
    x: padding + (availW - w) / 2,
    y: padding + (availH - h) / 2,
    w,
    h,
  };
}

/**
 * Compute the dimensions of a rectangle given its vertices.
 */
export function getRectangleDimensions(A: Point2D, B: Point2D, C: Point2D, D: Point2D) {
  if (!A || !B || !C || !D) {
    return { width: 0, height: 0 };
  }

  const getDist = (p1, p2) => Math.hypot(p2.x - p1.x, p2.y - p1.y);

  const distA = getDist(A, B);
  const distB = getDist(B, C);

  return {
    width: Math.max(distA, distB),
    height: Math.min(distA, distB),
  };
}

export function getGroundVertices(groundData: GroundType | undefined): GroundVertices {
  if (!groundData) {
    return {
      A: { x: 0, y: 0 },
      B: { x: 0, y: 0 },
      C: { x: 0, y: 0 },
      D: { x: 0, y: 0 },
    };
  }

  const isLPS = groundData.groundCoordsType === 'LOCAL';

  return {
    A: {
      x: (isLPS ? groundData.vertexAX?.value : groundData.vertexALatitude) || 0,
      y: (isLPS ? groundData.vertexAY?.value : groundData.vertexALongitude) || 0,
    },
    B: {
      x: (isLPS ? groundData.vertexBX?.value : groundData.vertexBLatitude) || 0,
      y: (isLPS ? groundData.vertexBY?.value : groundData.vertexBLongitude) || 0,
    },
    C: {
      x: (isLPS ? groundData.vertexCX?.value : groundData.vertexCLatitude) || 0,
      y: (isLPS ? groundData.vertexCY?.value : groundData.vertexCLongitude) || 0,
    },
    D: {
      x: (isLPS ? groundData.vertexDX?.value : groundData.vertexDLatitude) || 0,
      y: (isLPS ? groundData.vertexDY?.value : groundData.vertexDLongitude) || 0,
    },
  };
}

/**
 * Compute the canvas scale factors: how many canvas pixels correspond to one
 * unit of ground-space along each axis.
 *
 * Works regardless of:
 *  - coordinate type (local or GPS — longLen/shortLen are always in the same
 *    unit as the ground vertices after projection)
 *  - field rotation (longLen and shortLen are Euclidean lengths, rotation-invariant)
 *  - vertex order or winding
 *
 *  scaleU  pixels per ground-unit along the long  axis (A→D)
 *  scaleV  pixels per ground-unit along the short axis (A→B)
 *  scale   geometric mean — use this single value for elements with a fixed
 *          real-world size (circles, radii, line widths, icons…)
 *
 * Example — centre circle with FIFA radius 9.15 m:
 *
 *   const { scale } = computeScale(ctx, vp);
 *   const radiusPx  = 9.15 * scale;
 *
 * For GPS grounds, longLen/shortLen are in metres (equirectangular projection),
 * so `scale` is pixels-per-metre and radiusPx is immediately correct.
 * For local grounds the unit matches whatever the input coordinates used.
 */
export function computeScale(
  ctx: GroundContext,
  vp: Viewport,
): { scaleU: number; scaleV: number; scale: number } {
  // vp.w pixels span the full long axis  (longLen ground-units)
  // vp.h pixels span the full short axis (shortLen ground-units)
  const scaleU = vp.w / ctx.longLen;
  const scaleV = vp.h / ctx.shortLen;

  // Geometric mean: balances the two axes for isotropic elements.
  // For a perfect rectangle rendered without letterboxing scaleU === scaleV.
  // With letterboxing they differ slightly; the mean minimises the error for
  // circular/symmetric elements.
  const scale = Math.sqrt(scaleU * scaleV);

  return { scaleU, scaleV, scale };
}

/**
 * Calculates the Euclidean distance between two points in a 2D space.
 *
 * @param a The first point with x and y coordinates.
 * @param b The second point with x and y coordinates.
 * @return The distance between point a and point b.
 */
export function distanceLocal(a: Point2D, b: Point2D): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Calculates the canonical distance between two points in a given ground context.
 * The calculation scales the u and v coordinates based on the context's long and short lengths.
 *
 * @param a The first point, containing u and v coordinates.
 * @param b The second point, containing u and v coordinates.
 * @param ctx The ground context providing scaling factors for the u and v dimensions.
 * @return The calculated canonical distance between the two points.
 */
export function distanceCanonical(
  a: { u: number; v: number },
  b: { u: number; v: number },
  ctx: GroundContext,
): number {
  // ri-scala u e v nelle rispettive unità reali prima di calcolare
  const dx = (b.u - a.u) * ctx.longLen;
  const dy = (b.v - a.v) * ctx.shortLen;
  return Math.hypot(dx, dy);
}

/**
 * Calculates the great-circle distance between two geographical points
 * using their GPS coordinates based on the Haversine formula.
 *
 * @param a The first geographical point containing latitude and longitude.
 * @param b The second geographical point containing latitude and longitude.
 * @return The distance between the two points in meters.
 */
export function distanceGps(a: GpsPoint, b: GpsPoint): number {
  const R = 6_371_000; // metri
  const dLat = ((b.x - a.x) * Math.PI) / 180;
  const dLon = ((b.y - a.y) * Math.PI) / 180;
  const latA = (a.x * Math.PI) / 180;
  const latB = (b.x * Math.PI) / 180;

  const h = Math.sin(dLat / 2) ** 2 + Math.cos(latA) * Math.cos(latB) * Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(h)); // metri
}

/**
 * Compute the heading correction offset needed to convert between an absolute
 * compass heading (0° = North, clockwise) and a field-relative heading
 * (0° = toward goal D, i.e. along the long axis A→D).
 *
 * Background on conventions
 * ─────────────────────────
 * The ground context uses a standard math coordinate system:
 *   x grows rightward, y grows upward, angles are CCW from East.
 *
 * Compass headings use a geographic system:
 *   0° = North, 90° = East, grows clockwise.
 *
 * The long axis direction A→D in math angles (CCW from East) is:
 *   Math.atan2(D.y - A.y, D.x - A.x)   [radians]
 *
 * Converting that to a compass bearing (CW from North):
 *   bearing = 90 - mathAngleDeg         (East↔North swap + flip sign)
 *
 * fieldHeadingOffset is the compass bearing of the A→D axis.
 * Subtracting it from an absolute heading gives the heading relative to the
 * field's long axis (0° = toward D, 90° = toward the right touchline).
 *
 * Usage
 * ─────
 *   const { fieldHeadingOffset } = computeHeadingCorrection(ctx);
 *
 *   // absolute compass heading → field-relative heading
 *   const fieldHeading = normalizeAngle(compassHeading - fieldHeadingOffset);
 *
 *   // field-relative heading → absolute compass heading
 *   const compassHeading = normalizeAngle(fieldHeading + fieldHeadingOffset);
 *
 * If you only need absolute compass headings (tracker output, map display),
 * no correction is needed — use the raw heading value directly.
 *
 * @returns
 *   fieldHeadingOffset  compass bearing of the field's long axis A→D (degrees, 0–360)
 *   normalizeAngle      utility to keep any angle in [0, 360)
 */
export function computeHeadingCorrection(ctx: GroundContext): {
  fieldHeadingOffset: number;
  normalizeAngle: (deg: number) => number;
} {
  const { A, D } = ctx;

  // Math angle of A→D vector (CCW from East, degrees)
  const mathAngleDeg = (Math.atan2(D.y - A.y, D.x - A.x) * 180) / Math.PI;

  // Convert to compass bearing (CW from North)
  // bearing = 90 - mathAngle  →  then normalise to [0, 360)
  const fieldHeadingOffset = ((90 - mathAngleDeg) % 360 + 360) % 360;

  function normalizeAngle(deg: number): number {
    return ((deg % 360) + 360) % 360;
  }

  return { fieldHeadingOffset, normalizeAngle };
}
